import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Menu, Home, Calculator, Sun, Moon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { servicedCities } from "@/constants/cities";
import { useTheme } from "next-themes";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  const isCityPage = location.pathname.startsWith("/cities/");

  // Theme toggle
  const { theme, setTheme } = useTheme();
  const isDark = theme === "dark";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Updated navigation links to use anchor links for sections on the homepage
  const navigationLinks = [
    { path: isHomePage ? "/#hero" : "/", label: "Home", icon: Home },
    { path: isHomePage ? "/#about" : "/#about", label: "About" },
    { path: isHomePage ? "/#services" : "/#services", label: "Services & Prices", hasDropdown: true },
    { path: isHomePage ? "/#testimonials" : "/#testimonials", label: "Testimonials" },
    { path: isHomePage ? "/#locations" : "/#locations", label: "Locations", hasDropdown: true },
    { path: isHomePage ? "/#resources" : "/#resources", label: "Resources", hasDropdown: true },
  ];

  // Function to scroll to a section when an anchor link is clicked
  const scrollToSection = (event: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    if (isHomePage) {
      event.preventDefault();
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const getTextColor = (isActive: boolean) => {
    if (isActive) {
      return "text-primary-foreground font-bold";
    }
    if (isHomePage && !isScrolled) {
      return "text-primary-foreground";
    }
    return isScrolled ? "text-primary-foreground" : "text-primary";
  };

  // Define logo image metadata
  const logoImage = {
    src: "/images/logo/mobile-detail-co-logo.png",
    alt: "Mobile Detail Co. Logo - Professional Auto Detailing Services",
    width: 400,
    height: 120
  };

  return (
    <header
      className={`fixed w-full z-50 transition-all duration-300 ${
        isScrolled ? "bg-primary shadow-md py-2" : "bg-transparent py-4"
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="transform transition-transform hover:scale-105">
            <img
              src={logoImage.src}
              alt={logoImage.alt}
              width={logoImage.width}
              height={logoImage.height}
              fetchPriority="high"
              className={`transition-all duration-300 w-auto ${
                isScrolled ? "h-12 md:h-16" : "h-16 md:h-20"
              } object-contain`}
            />
          </Link>

          <nav className="hidden md:flex items-center space-x-8 justify-end flex-1 max-w-3xl ml-8">
            {navigationLinks.map(({ path, label, hasDropdown, icon: Icon }) => (
              hasDropdown ? (
                <DropdownMenu key={path}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className={`text-lg font-medium transition-all duration-300 transform hover:scale-105 hover:text-primary-foreground hover:bg-primary/80 ${
                        getTextColor(location.pathname === path)
                      }`}
                    >
                      {label}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    {path.includes('#services') && [
                      <DropdownMenuItem key="eco-wash" asChild>
                        <Link to="/#eco-wash" onClick={(e) => scrollToSection(e, 'eco-wash')}>ECO Wash Package</Link>
                      </DropdownMenuItem>,
                      <DropdownMenuItem key="complete-detail" asChild>
                        <Link to="/#complete-detail" onClick={(e) => scrollToSection(e, 'complete-detail')}>Complete Detail Package</Link>
                      </DropdownMenuItem>,
                      <DropdownMenuItem key="interior-detail" asChild>
                        <Link to="/#interior-detail" onClick={(e) => scrollToSection(e, 'interior-detail')}>Interior Detail Package</Link>
                      </DropdownMenuItem>,
                      <DropdownMenuItem key="eco-wax" asChild>
                        <Link to="/#eco-wax" onClick={(e) => scrollToSection(e, 'eco-wax')}>ECO Wax Package</Link>
                      </DropdownMenuItem>,
                      <DropdownMenuItem key="exterior-detailing" asChild>
                        <Link to="/#exterior-detailing" onClick={(e) => scrollToSection(e, 'exterior-detailing')}>Exterior Detailing Package</Link>
                      </DropdownMenuItem>,
                      <DropdownMenuItem key="ceramic-coatings" asChild>
                        <Link to="/#ceramic-coatings" onClick={(e) => scrollToSection(e, 'ceramic-coatings')}>Ceramic Coatings</Link>
                      </DropdownMenuItem>,
                    ]}
                    {path.includes('#locations') && [
                      ...servicedCities.map(city => (
                        <DropdownMenuItem key={city.path} asChild>
                          <Link to={city.path}>{city.name}</Link>
                        </DropdownMenuItem>
                      )),
                      <DropdownMenuItem key="areas-we-serve" asChild>
                        <Link to="/#areas-we-serve" onClick={(e) => scrollToSection(e, 'areas-we-serve')}>All Service Areas</Link>
                      </DropdownMenuItem>,
                    ]}
                    {path.includes('#resources') && [
                      <DropdownMenuItem key="faq" asChild>
                        <Link to="/#faq" onClick={(e) => scrollToSection(e, 'faq')}>FAQ</Link>
                      </DropdownMenuItem>,
                      <DropdownMenuItem key="blog" asChild>
                        <Link to="/#blog" onClick={(e) => scrollToSection(e, 'blog')}>Blog</Link>
                      </DropdownMenuItem>,
                    ]}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  key={path}
                  to={path}
                  onClick={(e) => {
                    if (path.includes('#')) {
                      const sectionId = path.split('#')[1];
                      scrollToSection(e, sectionId);
                    }
                  }}
                  className={`text-lg font-medium transition-all duration-300 transform hover:scale-105 hover:text-primary-foreground flex items-center gap-2 ${
                    getTextColor(location.pathname === path)
                  }`}
                >
                  {Icon && <Icon className="h-5 w-5" />}
                  {label}
                </Link>
              )
            ))}
            
            <Button 
              variant={isCityPage ? "default" : (isScrolled ? "outline" : "default")}
              className={`ml-4 font-medium ${
                isCityPage 
                  ? "bg-slate-800 text-white hover:bg-slate-700"
                  : isScrolled 
                    ? "border-primary-foreground text-primary hover:bg-primary-foreground hover:text-primary" 
                    : "bg-primary text-primary-foreground hover:bg-primary/90"
              }`}
              asChild
            >
              <Link to="/quote" className="flex items-center gap-2">
                <Calculator className="h-4 w-4" />
                Free Quote
              </Link>
            </Button>
            {/* Theme Switcher */}
            <button
              onClick={() => setTheme(isDark ? "light" : "dark")}
              className="ml-4 p-2 rounded-full bg-secondary hover:bg-secondary/80 text-secondary-foreground"
              aria-label="Toggle theme"
            >
              {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
          </nav>

          <div className="md:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  className={`p-8 h-20 w-20 ${isHomePage && !isScrolled ? "text-primary-foreground" : isScrolled ? "text-primary-foreground" : "text-primary"}`}
                >
                  <Menu size={24} strokeWidth={2} />
                </Button>
              </SheetTrigger>
              <SheetContent>
                <SheetHeader>
                  <SheetTitle>Menu</SheetTitle>
                </SheetHeader>
                <nav className="flex flex-col space-y-4 mt-4">
                  {navigationLinks.map(({ path, label, icon: Icon }) => (
                    <Link
                      key={path}
                      to={path}
                      onClick={(e) => {
                        if (path.includes('#')) {
                          const sectionId = path.split('#')[1];
                          scrollToSection(e, sectionId);
                        }
                      }}
                      className={`text-lg font-medium py-4 px-2 rounded transition-colors hover:text-primary hover:bg-accent flex items-center gap-3 ${
                        location.pathname === path ? "text-primary" : "text-primary"
                      }`}
                    >
                      {Icon && <Icon className="h-5 w-5" />}
                      {label}
                    </Link>
                  ))}
                  
                  <Link 
                    to="/quote" 
                    className={`text-lg font-medium py-4 px-2 rounded transition-colors flex items-center gap-3 justify-center ${
                      isCityPage 
                        ? "bg-slate-800 text-white hover:bg-slate-700"
                        : "hover:text-primary hover:bg-accent text-primary"
                    }`}
                  >
                    <Calculator className="h-5 w-5" />
                    Free Quote
                  </Link>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
